<template>
  <Form :title="$t('meta.baseData')" :bodyClass="''" @submit="submitBaseData">
    <FormInput v-model="vehicle.name" :name="$t('vehicles.name')" :required="true" />
    <FormInput v-model="vehicle.license_plate" :name="$t('vehicles.licencePlate')" :required="true" />
    <div class="form-wrap">
      <label for="description">{{ $t('meta.description') }}</label>
      <textarea
        v-model="vehicle.description"
        class="form-input"
        name="description"
        id="description"
        rows="5"
      ></textarea>
    </div>
    <FormInput v-model="vehicle.setup_duration" type="number" min="0" max="120" :name="$t('vehicles.setupDuration')" />
    <!-- Dropdown for vehicle_group with custom input -->
    <FormSelect
      v-model="vehicle.vehicle_group_name"
      :name="$t('vehicles.vehicleGroup')"
      :options="vehicleGroupOptions"
      :required="true"
    />
    <div v-if="vehicle.vehicle_group_name === 'other'">
      <FormInput
        v-model="vehicle.custom_vehicle_group"
        :name="$t('vehicles.customVehicleGroup')"
        :placeholder="$t('vehicles.vehicleGroupPlaceholder')"
      />
    </div>
    <!-- END Dropdown for vehicle_group with custom input -->

    <!-- Dropdown for manufacturer with custom input -->
    <FormSelect
      v-model="vehicle.manufacturer_name"
      :name="$t('vehicles.manufacturer')"
      :options="manufacturerOptions"
    />
    <div v-if="vehicle.manufacturer_name !== null && vehicle.manufacturer_name === 'other'">
      <FormInput
        v-model="vehicle.custom_manufacturer"
        :name="$t('vehicles.customManufacturer')"
        :placeholder="$t('vehicles.manufacturerPlaceholder')"
      />
    </div>
    <!-- END Dropdown for manufacturer with custom input -->
    <Fragment v-if="companyUser.company_type == 'industry'">
      <FormSelect
        v-model="vehicle.site_uuid"
        :name="$t('vehicles.plantSite')"
        :options="siteOptions"
        @input="onSiteChange"
      />
      <FormSelect
        v-model="vehicle.area_uuid"
        :name="$t('vehicles.plantArea')"
        :options="areaOptions"
        :placeholder="computedPlaceholder"
      />
    </Fragment>
    <Button type="submit" fullWidth>{{ $t('meta.save') }}</Button>
  </Form>
</template>

<script>
import { Fragment } from 'vue-fragment'

import Button from '@/components/Button/index.vue'
import Form from '@/components/Form'
import FormInput from '@/components/Form/Input'
import FormSelect from '@/components/Form/Select'

import areasApi from '@/api/areas'
import categoryValuesApi from '@/api/categoryValues'
import sitesApi from '@/api/sites'

export default {
  name: 'VehiclesBaseData',
  inject: ['companyUser'],
  components: {
    Button,
    Form,
    FormInput,
    FormSelect,
    Fragment,
  },
  props: {
    value: { type: Object, required: true },
    templateMode: { type: String, required: true },
  },
  data() {
    return {
      areas: [],
      vehicle: {
        site_uuid: null,
        area_uuid: null,
        vehicle_group_name: null,
        manufacturer_name: null,
        ...this.value,
      },
      manufacturerValues: [],
      vehicleGroupValues: [],
      sites: [],
    }
  },
  watch: {
    value: {
      handler(newValue) {
        const vehicle = {
          site_uuid: null,
          area_uuid: null,
          vehicle_group_name: null,
          manufacturer_name: null,
          ...newValue,
        }

        if (newValue.site) {
          vehicle.site_uuid = newValue.site.uuid
        }

        if (newValue.area) {
          vehicle.area_uuid = newValue.area.uuid
        }

        this.vehicle = vehicle

        if (this.vehicle.site_uuid) {
          this.getAreasForSite()
        }
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    vehicleGroupOptions() {
      return this.vehicleGroupValues.map(option => {
        return {
          value: option.name,
          label: option.localized_text,
        }
      })
    },
    manufacturerOptions() {
      return this.manufacturerValues.map(option => {
        return {
          value: option.name,
          label: option.localized_text,
        }
      })
    },
    siteOptions() {
      return this.sites.map(option => {
        return {
          value: option.uuid,
          label: option.name,
        }
      })
    },
    areaOptions() {
      return this.areas.map(option => {
        return {
          value: option.uuid,
          label: option.name,
        }
      })
    },
    computedPlaceholder() {
      const name = this.$t('vehicles.plantArea')
      if (!this.vehicle.site_uuid) return this.$t('vehicles.chooseSiteFirst')
      if (!this.areaOptions || !this.areaOptions.length) return this.$t('meta.noOptions', { name: name })
      return `${this.$t('meta.choose', { name: name })}`
    },
  },
  methods: {
    async getAreasForSite() {
      if (this.vehicle.site_uuid) {
        this.areas = await areasApi.getAll(this.vehicle.site_uuid)
      } else {
        this.vehicle.area_uuid = null
        this.areas = []
      }
    },
    async onSiteChange() {
      this.vehicle.area_uuid = null
      await this.getAreasForSite()
    },
    submitBaseData() {
      this.$emit('submit', this.vehicle)
    },
    async getVehicleGroupValues() {
      const result = await categoryValuesApi.get('vehicles', 'vehicle_group')

      if (!result) return

      this.vehicleGroupValues = result.sort((a, b) => {
        if (a.name === 'other' || b.name === 'other') {
          return 1
        } else {
          return a.text.toLowerCase() >= b.text.toLowerCase()
        }
      })
    },
    async getManufacturerValues() {
      const result = await categoryValuesApi.get('vehicles', 'manufacturer')

      if (!result) return

      this.manufacturerValues = result.sort((a, b) => {
        if (a.name === 'other' || b.name === 'other') {
          return 1
        } else {
          return a.text.toLowerCase() >= b.text.toLowerCase()
        }
      })
    },
  },
  async created() {
    if (this.companyUser.company_type == 'industry') {
      this.sites = await sitesApi.getAllForCompany(this.companyUser.company_uuid)
    }
    await this.getVehicleGroupValues()
    await this.getManufacturerValues()
  },
}
</script>
